import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { doc, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import toast from "react-hot-toast";

const MenuButton = styled.button`
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0.375rem;
  margin: -0.375rem -0.375rem -0.375rem 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.7;

  &:hover {
    background: rgba(199, 54, 89, 0.08);
    opacity: 1;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.25rem;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 9999;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.2s ease;
`;

const MenuContainer = styled.div`
  position: relative;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-text);
  font-size: 0.9rem;
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-bg);
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 16px;
    height: 16px;
    color: var(--color-text-light);
  }

  &.danger {
    color: #dc3545;

    svg {
      color: #dc3545;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 0.25rem 0;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-white);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 10001;
  max-width: 400px;
  width: 90%;
`;

const ConfirmBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
`;

const ConfirmTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.25rem;
`;

const ConfirmText = styled.p`
  margin-bottom: 1.5rem;
  color: var(--color-text-light);
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.cancel {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);

    &:hover {
      background: var(--color-bg);
    }
  }

  &.delete {
    background: #dc3545;
    color: white;

    &:hover {
      background: #c82333;
    }
  }
`;

// Icons
const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <title>More actions</title>
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>Edit</title>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>Delete</title>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
  </svg>
);

const PinIcon = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
  >
    <title>Pin</title>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>Copy link</title>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const StatsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <title>View stats</title>
    <path d="M18 20V10M12 20V4M6 20v-6" />
  </svg>
);

const MoreActionsMenu = ({
  item,
  collection = "briefly",
  onEdit,
  showAlways = false,
  commentCount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Delete the item from the specified collection
      await deleteDoc(doc(db, collection, item.id));
      toast.success("Deleted successfully");
      setShowDeleteConfirm(false);
      setIsOpen(false);
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePin = async () => {
    try {
      await updateDoc(doc(db, collection, item.id), {
        isPinned: !item.isPinned,
        pinnedAt: item.isPinned ? null : new Date(),
      });
      toast.success(item.isPinned ? "Unpinned" : "Pinned to top");
      setIsOpen(false);
    } catch (error) {
      console.error("Error pinning:", error);
      toast.error("Failed to pin");
    }
  };

  const handleCopyLink = () => {
    const path = window.location.pathname;
    const url = `${window.location.origin}${path}#${item.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
    setIsOpen(false);
  };

  const handleViewStats = () => {
    const likes = item.likeCount || 0;
    const comments =
      commentCount !== undefined ? commentCount : item.commentCount || 0;

    toast(
      `${likes} like${likes !== 1 ? "s" : ""}, ${comments} comment${
        comments !== 1 ? "s" : ""
      }`,
      {
        icon: "📊",
      }
    );
    setIsOpen(false);
  };

  return (
    <>
      <MenuContainer ref={menuRef} showAlways={showAlways}>
        <MenuButton
          onClick={() => setIsOpen(!isOpen)}
          aria-label="More actions"
        >
          <MoreIcon />
        </MenuButton>

        <Dropdown isOpen={isOpen} data-open={isOpen}>
          {onEdit && (
            <MenuItem
              onClick={() => {
                onEdit();
                setIsOpen(false);
              }}
            >
              <EditIcon />
              Edit
            </MenuItem>
          )}

          <MenuItem onClick={handlePin}>
            <PinIcon filled={item.isPinned} />
            {item.isPinned ? "Unpin" : "Pin to top"}
          </MenuItem>

          <MenuItem onClick={handleCopyLink}>
            <CopyIcon />
            Copy link
          </MenuItem>

          <MenuItem onClick={handleViewStats}>
            <StatsIcon />
            View stats
          </MenuItem>

          <Divider />

          <MenuItem
            className="danger"
            onClick={() => {
              setShowDeleteConfirm(true);
              setIsOpen(false);
            }}
          >
            <DeleteIcon />
            Delete
          </MenuItem>
        </Dropdown>
      </MenuContainer>

      {showDeleteConfirm && (
        <>
          <ConfirmBackdrop onClick={() => setShowDeleteConfirm(false)} />
          <ConfirmDialog>
            <ConfirmTitle>Delete this?</ConfirmTitle>
            <ConfirmText>
              This action cannot be undone. This will be permanently deleted.
            </ConfirmText>
            <ConfirmButtons>
              <Button
                className="cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="delete"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </ConfirmButtons>
          </ConfirmDialog>
        </>
      )}
    </>
  );
};

export default MoreActionsMenu;
